import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

// Assets APIs
export const assetsAPI = {
  getAll: (params) => api.get('/assets/', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets/', data),
  update: (id, data) => api.put(`/assets/${id}`),
  getByBarcode: (barcode) => api.get(`/assets/barcode/${barcode}`),
  addMaintenance: (id, data) => api.post(`/assets/${id}/maintenance`, data),
  addMovement: (id, data) => api.post(`/assets/${id}/movement`, data),
  getHistory: (id) => api.get(`/assets/${id}/history`),
  getStats: () => api.get('/assets/stats/overview'),
  // Bulk Import APIs
  downloadImportTemplate: async () => {
    const response = await api.get('/assets/import/template', {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `asset_import_template_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  },
  previewImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/assets/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/assets/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Labels APIs
export const labelsAPI = {
  getLabel: (assetId) => api.get(`/labels/${assetId}/label`),
  getLabelHTML: (assetId) => api.get(`/labels/${assetId}/label/html`),
  getBulkLabels: (assetIds) => api.post('/labels/bulk-labels', assetIds),
  scanBarcode: (barcode) => api.get(`/labels/barcode/${barcode}`),
};

// Clients APIs
export const clientsAPI = {
  getAll: (params) => api.get('/clients/', { params }),
  getById: (id) => api.get(`/clients/${id}/`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}/`, data),
  getRentals: (id) => api.get(`/clients/${id}/rentals/`),
  getInvoices: (id) => api.get(`/clients/${id}/invoices/`),
};

// Rentals APIs
export const rentalsAPI = {
  getAll: (params) => api.get('/rentals/', { params }),
  getById: (id) => api.get(`/rentals/${id}/`),
  create: (data) => api.post('/rentals/', data),
  updateStatus: (id, status) => api.put(`/rentals/${id}/status/`, null, { params: { status } }),
  getOverdue: () => api.get('/rentals/overdue/list/'),
  generateAgreement: (data) => api.post('/rentals/generate-agreement/', data, { responseType: 'blob' }),
  exportCSV: async () => {
    const response = await api.get('/rentals/export/csv', {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rentals_export_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  },
  exportJSON: async () => {
    const response = await api.get('/rentals/export/json', {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rentals_backup_${new Date().getTime()}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  }
};

// Invoices APIs
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices/', { params }),
  getById: (id) => api.get(`/invoices/${id}/`),
  create: (data) => api.post('/invoices/', data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payment/`, data),
  getOverdue: () => api.get('/invoices/overdue/list/'),
  generateRentalInvoice: (rentalId) => api.post(`/invoices/generate-rental-invoice/${rentalId}`),
  generateSaleInvoice: (saleId) => api.post(`/invoices/generate-sale-invoice/${saleId}`),
  downloadInvoice: async (invoiceId, invoiceType = 'rental') => {
    const endpoint = invoiceType === 'sale' 
      ? `/invoices/download-sale/${invoiceId}`
      : `/invoices/download-rental/${invoiceId}`;
    const response = await api.get(endpoint, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice_${invoiceId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return response;
  },
  getReminders: () => api.get('/invoices/reminders'),
};

// Financial APIs
export const financialAPI = {
  getDashboard: () => api.get('/financial/dashboard/'),
  getCashFlow: (params) => api.get('/financial/cash-flow/', { params }),
  getRevenueByClient: () => api.get('/financial/revenue-by-client/'),
  getProfitLoss: (params) => api.get('/financial/profit-loss/', { params }),
  getRealtimeCashflow: () => api.get('/financial/realtime-cashflow'),
  getOutstandingPayments: () => api.get('/financial/outstanding-payments'),
  aiQuery: (data) => api.post('/financial/ai-query', data),
  getAnomalyDetection: () => api.get('/financial/anomaly-detection')
};

// Asset Replacement APIs
export const replacementsAPI = {
  create: (data) => api.post('/replacements/', null, { params: data }),
  getByRental: (rentalId) => api.get(`/replacements/rental/${rentalId}`),
  getByAsset: (assetId) => api.get(`/replacements/asset/${assetId}`),
  getAll: (params) => api.get('/replacements/', { params })
};

// Analytics APIs
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard/'),
  getTrends: (params) => api.get('/analytics/trends/', { params }),
  getAssetPerformance: () => api.get('/analytics/asset-performance/'),
};

// AI APIs
export const aiAPI = {
  query: (data) => api.post('/ai/query/', data),
  detectAnomalies: () => api.get('/ai/anomaly-detection/'),
  getPredictiveInsights: () => api.get('/ai/predictive-insights/'),
};

// Marketing APIs
export const marketingAPI = {
  createTarget: (data) => api.post('/marketing/targets/', data),
  // Marketing Performance APIs
  uploadAd: (data) => api.post('/marketing-performance/ads/upload', data),
  getDailyProgress: (date) => api.get('/marketing-performance/ads/daily-progress', { params: { date } }),
  getTeamProgress: (date) => api.get('/marketing-performance/ads/team-progress', { params: { date } }),
  assignClient: (data) => api.post('/marketing-performance/clients/assign', data),
  calculateCommissions: () => api.get('/marketing-performance/commissions/calculate'),
  getLeaderboard: () => api.get('/marketing-performance/performance/leaderboard'),
  getIndividualPerformance: (id) => api.get(`/marketing-performance/performance/individual/${id}`),
  getTopAds: (limit) => api.get('/marketing-performance/ads/top-performing', { params: { limit } }),
  getTargets: (params) => api.get('/marketing/targets/', { params }),
  updateTarget: (id, data) => api.put(`/marketing/targets/${id}/`, data),
  getPerformance: (id) => api.get(`/marketing/performance/${id}/`),
  calculateBonus: (id) => api.get(`/marketing/bonus-calculation/${id}/`),
  // Edit & Personalize APIs
  updatePerson: (id, data) => api.put(`/marketing-performance/person/${id}`, data),
  deletePerson: (id) => api.delete(`/marketing-performance/person/${id}`),
  markCommissionPaid: (commissionId, paymentDate) => api.put(`/marketing-performance/commission/${commissionId}/mark-paid`, null, { params: { payment_date: paymentDate } })
};

// Sales APIs
export const salesAPI = {
  create: (data) => api.post('/sales/', data),
  getAll: (limit) => api.get('/sales/', { params: { limit } }),
  getStats: () => api.get('/sales/stats'),
  getById: (id) => api.get(`/sales/${id}`),
  cancel: (id) => api.delete(`/sales/${id}`),
};

// Documents APIs
export const documentsAPI = {
  getAll: (params) => api.get('/documents/', { params }),
  getById: (id) => api.get(`/documents/${id}/`),
  create: (data) => api.post('/documents/', data),
  getByClient: (clientId) => api.get(`/documents/client/${clientId}/`),
  generateProposal: (data) => api.post('/documents/proposal/', data),
};

// Notifications APIs
export const notificationsAPI = {
  sendEmail: (data) => api.post('/notifications/send-email/', data),
  sendSMS: (data) => api.post('/notifications/send-sms/', data),
  sendWhatsApp: (data) => api.post('/notifications/send-whatsapp/', data),
  sendPaymentReminders: () => api.get('/notifications/payment-reminders/'),
  getHistory: (params) => api.get('/notifications/history/', { params }),
};
